import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import authService from '../services/auth';
import EditableDropdown from './EditableDropdown';
import CreateItemDialog from './CreateItemDialog';
import ConfirmationDialog from './ConfirmationDialog';
import SimilarClientDialog from './SimilarClientDialog';
import './FolderStructureBuilder.css';

const FolderStructureBuilder = () => {
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [shots, setShots] = useState([]);
  
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedSequence, setSelectedSequence] = useState('');
  const [selectedShot, setSelectedShot] = useState('');
  
  const [dialogState, setDialogState] = useState({
    type: null, // 'client', 'job', 'sequence', 'shot'
    isOpen: false,
    step: 0,
    values: {}
  });

  const [confirmationState, setConfirmationState] = useState({
    isOpen: false,
    jobs: 0,
    jobPath: '',
    sequences: 0,
    shots: 0,
    applications: 0,
    isError: false,
    message: ''
  });

  const [similarClientState, setSimilarClientState] = useState({
    isOpen: false,
    inputName: '',
    suggestions: []
  });
  
  const [selectedApps, setSelectedApps] = useState({
    aftereffects: false,
    photoshop: false,
    illustrator: false,
    animate: false,
    cinema4d: false,
    houdini: false,
    blender: false,
    zbrush: false
  });

  const [loading, setLoading] = useState(false);
  const [tempShotRange, setTempShotRange] = useState('');
  const [temporaryShotEntries, setTemporaryShotEntries] = useState([]);
  const [appsChanged, setAppsChanged] = useState(false);
  
  // Local storage for items not yet created
  const [localClients, setLocalClients] = useState([]);
  const [localJobs, setLocalJobs] = useState({});  // key: clientName, value: array of job names
  const [localSequences, setLocalSequences] = useState({}); // key: clientName/jobName, value: array of sequence names

  // Queued items for preview
  const [queuedJobs, setQueuedJobs] = useState([]); // array of job names queued for creation
  const [queuedSequences, setQueuedSequences] = useState([]);
  const [queuedShots, setQueuedShots] = useState({});
  
  // All shots for all sequences (for preview)
  const [allSequenceShots, setAllSequenceShots] = useState({});
  
  // Store existing applications for each shot (key: "client/job/sequence/shot", value: array of app keys)
  const [shotExistingApps, setShotExistingApps] = useState({});

  // Dialog field configurations
  const dialogConfigs = {
    client: {
      title: 'Create New Client',
      fields: [{
        name: 'clientName',
        placeholder: 'Client Name',
        rules: [
          'Letters, numbers, spaces, and hyphens only',
          'Example: Adobe, Google, The Heist'
        ]
      }]
    },
    job: {
      title: 'Create New Job',
      fields: [
        {
          name: 'jobNumber',
          placeholder: 'Job Number',
          rules: [
            'Format: ##-###',
            'Example: 25-123'
          ]
        },
        {
          name: 'jobName',
          placeholder: 'Job Name',
          rules: [
            'Letters, numbers, and hyphens only',
            'No spaces or underscores',
            'Example: ProjectName, Movie-Title'
          ]
        }
      ]
    },
    sequence: {
      title: 'Create New Sequence',
      fields: [{
        name: 'sequenceName',
        placeholder: 'Sequence Name',
        maxLength: 8,
        rules: [
          'Maximum 8 characters',
          'Letters, numbers, and hyphens only',
          'Example: MOE, Intro, Seq01'
        ]
      }]
    },
    shot: {
      title: 'Create New Shot',
      fields: [
        {
          name: 'shotName',
          placeholder: 'Shot Name or Pattern',
          rules: [
            'Single shot: 1',
            'Range: 10-100',
            'List: 1,2,3 or 1, 2, 3',
            'Custom name: letters, numbers, or hyphens only'
          ]
        },
        {
          name: 'shotIncrement',
          placeholder: 'Increment (default: 10)',
          rules: [
            'Increment for range creation',
            'Default: 10 (creates 10, 20, 30, etc.)',
            'Custom: 5 (creates 10, 15, 20, etc.)'
          ]
        }
      ]
    }
  };

  // Validation functions
  const validateClientName = (name) => {
    const regex = /^[a-zA-Z0-9\s-]+$/;
    return regex.test(name);
  };

  const validateJobNumber = (number) => {
    const regex = /^\d{2}-\d{3}$/;
    return regex.test(number);
  };

  const validateJobName = (name) => {
    const regex = /^[a-zA-Z0-9-]+$/;
    return regex.test(name);
  };

  const validateSequenceName = (name) => {
    const regex = /^[a-zA-Z0-9-]{1,8}$/;
    return regex.test(name);
  };

  const validateShotName = (name) => {
    // Single number: 1
    if (/^\d+$/.test(name)) return true;
    // Range: 1-10
    if (/^\d+-\d+$/.test(name)) return true;
    // List: 1,2,3 or 1, 2, 3
    if (/^\d+(\s*,\s*\d+)*$/.test(name)) return true;
    // Custom alphanumeric with hyphens (no spaces or underscores)
    if (/^[a-zA-Z0-9-]+$/.test(name)) return true;
    return false;
  };

  const isRangePattern = (shotName) => {
    return /^\d+-\d+$/.test(shotName);
  };

  const isCommaList = (shotName) => {
    return shotName.includes(',');
  };

  const validateShotIncrement = (increment) => {
    const num = parseInt(increment);
    return !isNaN(num) && num > 0;
  };

  const getValidationMessage = (field) => {
    switch (field) {
      case 'client':
        return 'Client names can only contain letters, numbers, spaces, and hyphens';
      case 'jobNumber':
        return 'Job number format: ##-### (e.g., 24-001)';
      case 'jobName':
        return 'Job names can only contain letters, numbers, and hyphens (no spaces or underscores)';
      case 'sequence':
        return 'Sequence: max 8 characters, letters/numbers/hyphens only';
      case 'shot':
        return 'Shot format: number (1), range (1-10), list (1,2,3), or custom name (letters/numbers/hyphens)';
      case 'shotIncrement':
        return 'Increment must be a positive number (e.g., 10, 5, 1)';
      default:
        return '';
    }
  };

  // Application order and display names (ordered for 2-column layout: top-down, left-right)
  const appOrder = ['aftereffects', 'cinema4d', 'photoshop', 'zbrush', 'illustrator', 'blender', 'animate', 'houdini'];
  const appDisplayNames = {
    aftereffects: 'After Effects',
    photoshop: 'Photoshop',
    illustrator: 'Illustrator',
    animate: 'Animate',
    cinema4d: 'Cinema 4D',
    houdini: 'Houdini',
    blender: 'Blender',
    zbrush: 'Zbrush'
  };
  
  const appAbbreviations = {
    aftereffects: 'AE',
    photoshop: 'PS',
    illustrator: 'ILL',
    animate: 'AN',
    cinema4d: 'C4D',
    houdini: 'HOU',
    blender: 'BLD',
    zbrush: 'ZBR'
  };
  

  const loadClients = async () => {
    try {
      const fsClients = await apiService.getClients();
      // Combine file system clients with local clients
      const allClients = [...new Set([...fsClients, ...localClients])];
      setClients(allClients.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  // Define callback functions first
  const loadAllSequenceShots = useCallback(async (clientName, jobName, sequencesList) => {
    const allShots = {};
    for (const seq of sequencesList) {
      try {
        const shotsData = await apiService.getShots(clientName, jobName, seq);
        allShots[seq] = shotsData;
      } catch (error) {
        console.error(`Error loading shots for sequence ${seq}:`, error);
        allShots[seq] = [];
      }
    }
    setAllSequenceShots(allShots);
    
    // Don't load queued items automatically - only load when explicitly requested
  }, []);

  const loadJobs = useCallback(async (clientName) => {
    if (!clientName) return;
    try {
      const fsJobs = await apiService.getJobs(clientName);
      // Combine file system jobs with local jobs for this client
      const localClientJobs = localJobs[clientName] || [];
      const allJobs = [...new Set([...fsJobs, ...localClientJobs])];
      setJobs(allJobs.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
      setSequences([]);
      setShots([]);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  }, [localJobs]);

  const loadSequences = useCallback(async (clientName, jobName) => {
    if (!clientName || !jobName) return;
    try {
      const fsSequences = await apiService.getSequences(clientName, jobName);
      // Combine file system sequences with local sequences for this job
      const jobKey = `${clientName}/${jobName}`;
      const localJobSequences = localSequences[jobKey] || [];
      const allSequences = [...new Set([...fsSequences, ...localJobSequences])];
      setSequences(allSequences.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
      setShots([]);
      // Load all shots for all sequences for preview
      if (allSequences.length > 0) {
        await loadAllSequenceShots(clientName, jobName, allSequences);
      }
    } catch (error) {
      console.error('Failed to load sequences:', error);
    }
  }, [localSequences, loadAllSequenceShots]);

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload clients when local clients change
  useEffect(() => {
    if (localClients.length > 0) {
      loadClients();
    }
  }, [localClients]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload jobs when local jobs change and client is selected
  useEffect(() => {
    if (selectedClient) {
      loadJobs(selectedClient);
    }
  }, [localJobs, selectedClient, loadJobs]);

  // Reload sequences when local sequences change and job is selected
  useEffect(() => {
    if (selectedClient && selectedJob) {
      loadSequences(selectedClient, selectedJob);
    }
  }, [localSequences, selectedClient, selectedJob, loadSequences]);

  // Load existing applications for shots that are displayed in preview
  useEffect(() => {
    const loadExistingAppsForPreview = async () => {
      if (!selectedClient || !selectedJob) return;

      // Only load for shots that exist in allSequenceShots
      for (const [sequenceName, shots] of Object.entries(allSequenceShots)) {
        for (const shotName of shots) {
          const shotKey = `${selectedClient}/${selectedJob}/${sequenceName}/${shotName}`;
          
          // Only load if we don't already have the data
          if (!shotExistingApps[shotKey]) {
            try {
              await loadExistingApplications(selectedClient, selectedJob, sequenceName, shotName);
              // Add a small delay to avoid overwhelming the server
              await new Promise(resolve => setTimeout(resolve, 50));
            } catch (error) {
              console.error(`Failed to load apps for ${shotKey}:`, error);
            }
          }
        }
      }
    };

    loadExistingAppsForPreview();
  }, [allSequenceShots, selectedClient, selectedJob]); // eslint-disable-line react-hooks/exhaustive-deps


  const loadShots = async (clientName, jobName, sequenceName) => {
    if (!clientName || !jobName || !sequenceName) return;
    try {
      const shotList = await apiService.getShots(clientName, jobName, sequenceName);
      setShots(shotList);
    } catch (error) {
      console.error('Failed to load shots:', error);
    }
  };


  const loadQueuedItems = async (clientName, jobName) => {
    // Separate function to load queued items only when needed
    try {
      const queuedData = await apiService.getQueuedItems(clientName, jobName);
      // Transform sequence names into objects with sequence property
      const sequenceObjects = Object.keys(queuedData).map(seqName => ({ sequence: seqName }));
      setQueuedSequences(sequenceObjects);
      
      // Preserve existing shot objects and only add applications to new shots
      setQueuedShots(prevQueuedShots => {
        const queuedShotsWithApps = {};
        const currentApps = Object.keys(selectedApps).filter(app => selectedApps[app]);
        
        for (const [seqName, shotNames] of Object.entries(queuedData)) {
          const existingShotsInSeq = prevQueuedShots[seqName] || [];
          
          queuedShotsWithApps[seqName] = shotNames.map(shotName => {
            // If shot already exists, preserve its applications
            const existingShot = existingShotsInSeq.find(shotObj => shotObj.shot === shotName);
            if (existingShot) {
              return existingShot; // Keep existing shot with its original applications
            } else {
              // New shot, assign current applications
              return {
                shot: shotName,
                applications: currentApps
              };
            }
          });
        }
        
        return queuedShotsWithApps;
      });
    } catch (error) {
      console.error('Error loading queued items:', error);
      setQueuedSequences([]);
      setQueuedShots({});
    }
  };

  const loadExistingApplications = async (clientName, jobName, sequenceName, shotName) => {
    if (!clientName || !jobName || !sequenceName || !shotName) return [];
    
    try {
      const shotKey = `${clientName}/${jobName}/${sequenceName}/${shotName}`;
      const response = await apiService.getExistingApplications(clientName, jobName, sequenceName, shotName);
      const existingApps = response.applications || [];
      
      // Update the state with existing applications for this shot
      setShotExistingApps(prev => ({
        ...prev,
        [shotKey]: existingApps
      }));
      
      return existingApps;
    } catch (error) {
      console.error('Error loading existing applications:', error);
      return [];
    }
  };

  const handleClientChange = (value) => {
    setSelectedClient(value);
    setSelectedJob('');
    setSelectedSequence('');
    setSelectedShot('');
    setTempShotRange('');
    setTemporaryShotEntries([]);
    
    if (value) {
      loadJobs(value);
    }
  };

  const handleJobChange = (value) => {
    setSelectedJob(value);
    setSelectedSequence('');
    setSelectedShot('');
    setTempShotRange('');
    // Clear queued items when changing jobs
    setQueuedJobs([]);
    setQueuedSequences([]);
    setQueuedShots({});
    setTemporaryShotEntries([]);
    // Reset apps changed flag
    setAppsChanged(false);
    
    if (value && selectedClient) {
      // Clear backend memory storage for this job to start fresh (don't block UI)
      apiService.clearQueuedItems(selectedClient, value).catch(error => {
        console.error('Failed to clear memory storage:', error);
      });
      
      loadSequences(selectedClient, value);
    }
  };

  const handleSequenceChange = (value) => {
    setSelectedSequence(value);
    setSelectedShot('');
    setTempShotRange('');
    // Clear temporary entries when changing sequence
    setTemporaryShotEntries([]);
    
    if (value && selectedClient && selectedJob) {
      loadShots(selectedClient, selectedJob, value);
    }
  };

  const handleShotChange = (value) => {
    console.log('Shot changed to:', value);
    
    // If user selects a different shot (not the temporary entry), clear temporary entries
    if (temporaryShotEntries.length > 0 && !temporaryShotEntries.includes(value)) {
      console.log('Clearing temporary shot entries');
      const tempShot = temporaryShotEntries[0];
      
      // Remove the temporary shot from the regular shots list if it's a range or comma-separated format
      if (tempShot && (tempShot.includes('-') || tempShot.includes(',') || tempShot.includes(':'))) {
        setShots(prev => prev.filter(shot => shot !== tempShot));
      }
      
      setTemporaryShotEntries([]);
    }
    
    // Clear temp shot range when selecting any real shot
    if (tempShotRange) {
      console.log('Clearing tempShotRange');
      setTempShotRange('');
    }
    
    setSelectedShot(value);
    // Mark that apps have changed when shot changes
    setAppsChanged(true);
  };

  // Dialog handlers
  const openDialog = (type) => {
    setDialogState({
      type,
      isOpen: true,
      step: 0,
      values: {}
    });
  };

  const closeDialog = () => {
    setDialogState({
      type: null,
      isOpen: false,
      step: 0,
      values: {}
    });
  };

  const handleDialogSubmit = async (value, fieldName) => {
    const { type, step, values } = dialogState;
    const config = dialogConfigs[type];
    
    // Validate the input
    let isValid = false;
    switch (fieldName) {
      case 'clientName':
        isValid = validateClientName(value);
        if (!isValid) alert(getValidationMessage('client'));
        break;
      case 'jobNumber':
        isValid = validateJobNumber(value);
        if (!isValid) alert(getValidationMessage('jobNumber'));
        break;
      case 'jobName':
        isValid = validateJobName(value);
        if (!isValid) alert(getValidationMessage('jobName'));
        break;
      case 'sequenceName':
        isValid = validateSequenceName(value);
        if (!isValid) alert(getValidationMessage('sequence'));
        break;
      case 'shotName':
        isValid = validateShotName(value);
        if (!isValid) alert(getValidationMessage('shot'));
        break;
      case 'shotIncrement':
        isValid = validateShotIncrement(value);
        if (!isValid) alert(getValidationMessage('shotIncrement'));
        break;
      default:
        isValid = true;
    }
    
    if (!isValid) return;
    
    const newValues = { ...values, [fieldName]: value };
    
    // Special handling for shots: if it's a range pattern, proceed to increment step
    if (type === 'shot' && fieldName === 'shotName' && isRangePattern(value)) {
      setDialogState(prev => ({
        ...prev,
        step: 1, // Go to increment step
        values: newValues
      }));
      return;
    }
    
    // For shot ranges, if we're on the increment step or it's not a range, we're done
    // For other types, check if there are more steps
    const shouldContinue = type === 'shot' 
      ? false // Shot is either done (non-range) or done (range with increment)
      : step < config.fields.length - 1;
      
    if (shouldContinue) {
      setDialogState(prev => ({
        ...prev,
        step: step + 1,
        values: newValues
      }));
      return;
    }
    
    // Final step - store the item in memory only
    try {
      switch (type) {
        case 'client':
          // Check for similar clients before creating
          try {
            const similarResult = await apiService.checkSimilarClients(newValues.clientName);
            if (similarResult.hasSuggestions && similarResult.suggestions.length > 0) {
              // Show similar clients dialog
              setSimilarClientState({
                isOpen: true,
                inputName: newValues.clientName,
                suggestions: similarResult.suggestions
              });
              return; // Don't proceed with creation yet
            }
          } catch (error) {
            console.warn('Failed to check similar clients:', error);
            // Continue with creation if API fails
          }
          
          // No similar clients found, proceed with creation
          await createNewClient(newValues.clientName);
          break;
        case 'job':
          const fullJobName = `${newValues.jobNumber}_${newValues.jobName}`;
          // Store job locally, don't send to backend yet
          setLocalJobs(prev => ({
            ...prev,
            [selectedClient]: [...new Set([...(prev[selectedClient] || []), fullJobName])]
          }));
          await loadJobs(selectedClient);
          setSelectedJob(fullJobName);
          // Reset subsequent selections when adding new job
          setSelectedSequence('');
          setSelectedShot('');
          setTempShotRange('');
          // Clear the lists for the new job
          setSequences([]);
          setShots([]);
          // Load sequences for the new job
          await loadSequences(selectedClient, fullJobName);
          break;
        case 'sequence':
          // Store sequence locally, don't send to backend yet
          const jobKey = `${selectedClient}/${selectedJob}`;
          setLocalSequences(prev => ({
            ...prev,
            [jobKey]: [...new Set([...(prev[jobKey] || []), newValues.sequenceName])]
          }));
          await loadSequences(selectedClient, selectedJob);
          setSelectedSequence(newValues.sequenceName);
          // Reset subsequent selections when adding new sequence
          setSelectedShot('');
          setTempShotRange('');
          // Clear the shots list for the new sequence
          setShots([]);
          // Load shots for the new sequence
          await loadShots(selectedClient, selectedJob, newValues.sequenceName);
          break;
        case 'shot':
          // Handle different shot input patterns - store locally, don't send to backend yet
          let displayEntry = newValues.shotName;
          
          if (isRangePattern(newValues.shotName) && newValues.shotIncrement) {
            // Range pattern with increment (e.g., "10-100" with increment 10)
            displayEntry = `${newValues.shotName}:${parseInt(newValues.shotIncrement) || 10}`;
          } else if (isCommaList(newValues.shotName)) {
            // Comma-separated list (e.g., "6, 9, 8,10, 11")
            displayEntry = newValues.shotName;
          } else {
            // Single shot or other format
            displayEntry = newValues.shotName;
          }
          
          // Add to temporary entries for dropdown display (but not queued yet)
          setTemporaryShotEntries([displayEntry]);
          
          // Set the selected shot to the display entry
          setSelectedShot(displayEntry);
          
          // Clear temp shot range 
          setTempShotRange('');
          
          // Don't reload shots or queue yet - wait for Queue Folders button
          break;
        default:
          console.error('Unknown dialog type:', type);
          break;
      }
      closeDialog();
    } catch (error) {
      console.error(`Failed to create ${type}:`, error);
      alert(`Failed to create ${type}. Please try again.`);
    }
  };

  // Helper function to create a new client
  const createNewClient = async (clientName) => {
    // Store client locally, don't send to backend yet
    setLocalClients(prev => [...new Set([...prev, clientName])]);
    await loadClients(); // Reload to show local client
    setSelectedClient(clientName);
    // Reset all subsequent selections when adding new client
    setSelectedJob('');
    setSelectedSequence('');
    setSelectedShot('');
    setTempShotRange('');
    // Clear the lists for the new client
    setJobs([]);
    setSequences([]);
    setShots([]);
    // Load jobs for the new client
    await loadJobs(clientName);
  };

  // Handle similar client dialog actions
  const handleSimilarClientClose = () => {
    setSimilarClientState({ isOpen: false, inputName: '', suggestions: [] });
  };

  const handleSelectSuggestion = async (clientName) => {
    setSelectedClient(clientName);
    // Reset subsequent selections
    setSelectedJob('');
    setSelectedSequence('');
    setSelectedShot('');
    setTempShotRange('');
    // Load jobs for the selected client
    await loadJobs(clientName);
    handleSimilarClientClose();
    closeDialog(); // Also close the main create dialog
  };

  const handleCreateNewFromDialog = async () => {
    const clientName = similarClientState.inputName;
    await createNewClient(clientName);
    handleSimilarClientClose();
    closeDialog(); // Also close the main create dialog
  };


  const handleAppToggle = (app) => {
    setSelectedApps(prev => ({
      ...prev,
      [app]: !prev[app]
    }));
    // Mark that apps have changed
    setAppsChanged(true);
  };

  const handleSelectAllApps = () => {
    setSelectedApps(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(app => {
        newState[app] = true;
      });
      return newState;
    });
    // Mark that apps have changed
    setAppsChanged(true);
  };

  const handleDeselectAllApps = () => {
    setSelectedApps(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(app => {
        newState[app] = false;
      });
      return newState;
    });
    // Mark that apps have changed
    setAppsChanged(true);
  };

  const handleQueueItems = async () => {
    console.log('=== handleQueueItems called ===');
    console.log('Selected:', { client: selectedClient, job: selectedJob, sequence: selectedSequence, shot: selectedShot });
    console.log('Selected Apps:', selectedApps);
    console.log('canAddAppsToExistingShot:', canAddAppsToExistingShot());

    // This function will queue the current client/job selection (sequence/shot optional)
    if (selectedClient && selectedJob) {
      try {
        setLoading(true);

        // First, send any local items to backend
        // NOTE: Removed createClient and createJob calls - the batch endpoint will handle these

        // 1. Track client if it's local (will be created by batch endpoint)
        // 2. Track job if it's local (will be created by batch endpoint)

        // If no sequence/shot selected, just create the job structure
        if (!selectedSequence || !selectedShot) {
          // Add job to queued jobs if not already there
          setQueuedJobs(prev => {
            if (!prev.includes(selectedJob)) {
              return [...prev, selectedJob];
            }
            return prev;
          });
          // Reset apps changed flag after queuing
          setAppsChanged(false);
          return;
        }

        // 3. Handle shot data - either from temporary entries (new shots) or existing shot
        const hasTemporaryEntries = temporaryShotEntries.length > 0;
        const canAddAppsToExisting = canAddAppsToExistingShot();
        let shotData, increment = null;

        if (hasTemporaryEntries) {
          // New shots from temporary entries
          const shotEntry = temporaryShotEntries[0];

          // Parse the shot entry to extract original data
          if (shotEntry.includes(':')) {
            // Range pattern like "10-100:10"
            const [range, inc] = shotEntry.split(':');
            shotData = range;
            increment = parseInt(inc);
          } else {
            // Regular shot data
            shotData = shotEntry;
          }

          // Send to backend to create shots in memory
          await apiService.createShot(selectedClient, selectedJob, selectedSequence, shotData, increment);
        } else if (canAddAppsToExisting) {
          // Existing shot(s) - add applications
          const selectedAppsList = Object.keys(selectedApps).filter(app => selectedApps[app]);

          if (selectedShot === "All Shots") {
            // Handle "All Shots" - add applications to all existing shots in the sequence
            const existingShots = allSequenceShots[selectedSequence] || [];

            for (const shotName of existingShots) {
              // Load existing applications for each shot
              await loadExistingApplications(selectedClient, selectedJob, selectedSequence, shotName);

              // Add each shot to the queuedShots state
              setQueuedShots(prev => {
                const updated = { ...prev };
                if (!updated[selectedSequence]) {
                  updated[selectedSequence] = [];
                }
                // Check if shot already exists in queue
                const existingQueuedShot = updated[selectedSequence].find(shot => shot.shot === shotName);

                if (existingQueuedShot) {
                  // Merge new apps with existing queued apps (avoid duplicates)
                  const mergedApps = [...new Set([...existingQueuedShot.applications, ...selectedAppsList])];
                  // Update the existing entry
                  updated[selectedSequence] = updated[selectedSequence].map(shot =>
                    shot.shot === shotName
                      ? { ...shot, applications: mergedApps }
                      : shot
                  );
                } else {
                  // Add new entry
                  updated[selectedSequence].push({
                    shot: shotName,
                    applications: selectedAppsList
                  });
                }
                return updated;
              });
            }
          } else {
            // Handle single shot
            shotData = selectedShot;

            // Load existing applications for this shot before queuing
            await loadExistingApplications(selectedClient, selectedJob, selectedSequence, selectedShot);

            // Add this shot to the queuedShots state directly since it's for an existing shot
            console.log('About to queue apps for existing shot:', {
              sequence: selectedSequence,
              shot: selectedShot,
              selectedApps: selectedAppsList
            });

            setQueuedShots(prev => {
              const updated = { ...prev };
              console.log('Current queuedShots state:', JSON.parse(JSON.stringify(prev)));

              if (!updated[selectedSequence]) {
                updated[selectedSequence] = [];
              }
              // Check if shot already exists in queue
              const existingQueuedShot = updated[selectedSequence].find(shot => shot.shot === selectedShot);
              console.log('Existing queued shot found:', existingQueuedShot);

              if (existingQueuedShot) {
                // Merge new apps with existing queued apps (avoid duplicates)
                const mergedApps = [...new Set([...existingQueuedShot.applications, ...selectedAppsList])];
                console.log('Merging apps:', {
                  existing: existingQueuedShot.applications,
                  selected: selectedAppsList,
                  merged: mergedApps
                });
                // Update the existing entry
                updated[selectedSequence] = updated[selectedSequence].map(shot =>
                  shot.shot === selectedShot
                    ? { ...shot, applications: mergedApps }
                    : shot
                );
              } else {
                console.log('Adding new shot to queue');
                // Add new entry
                updated[selectedSequence].push({
                  shot: selectedShot,
                  applications: selectedAppsList
                });
              }
              console.log('Updated queuedShots state:', JSON.parse(JSON.stringify(updated)));
              return updated;
            });
          }
        }

        // Move temporary shot entries to regular shots list so they remain visible in dropdown
        if (hasTemporaryEntries) {
          const tempShot = temporaryShotEntries[0];
          setShots(prev => [...new Set([...prev, tempShot])]);
          setTemporaryShotEntries([]);
        }

        // Keep shot selection after queuing (don't reset unless user changes hierarchy)

        // For new shots, reload queued items from backend
        // For existing shots, the state was updated directly above
        if (hasTemporaryEntries && selectedClient && selectedJob) {
          await loadQueuedItems(selectedClient, selectedJob);
        }

        // Reset apps changed flag after queuing
        setAppsChanged(false);

      } catch (error) {
        console.error('Failed to queue items:', error);
        alert('Failed to queue items. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const removeQueuedSequence = (index) => {
    const sequenceToRemove = queuedSequences[index].sequence;
    // Remove sequence from queue
    setQueuedSequences(prev => prev.filter((_, i) => i !== index));
    // Remove all shots associated with this sequence
    setQueuedShots(prev => {
      const updated = { ...prev };
      delete updated[sequenceToRemove];
      return updated;
    });
    // Mark that apps have changed when removing queued items
    setAppsChanged(true);
  };

  const removeQueuedShot = (sequenceName, shotName) => {
    setQueuedShots(prev => {
      const updated = { ...prev };
      if (updated[sequenceName]) {
        updated[sequenceName] = updated[sequenceName].filter(shotObj => shotObj.shot !== shotName);
        // If no shots left in this sequence, remove the sequence entirely
        if (updated[sequenceName].length === 0) {
          delete updated[sequenceName];
        }
      }
      return updated;
    });
    // Mark that apps have changed when removing queued items
    setAppsChanged(true);
  };

  const refreshPreview = async () => {
    if (!selectedClient || !selectedJob) return;
    
    try {
      // Refresh sequences
      await loadSequences(selectedClient, selectedJob);
      
      // Refresh shots for current sequence if selected
      if (selectedSequence) {
        await loadShots(selectedClient, selectedJob, selectedSequence);
      }
      
      // Refresh existing applications for all shots in current sequence
      if (selectedSequence) {
        const currentShots = allSequenceShots[selectedSequence] || [];
        for (const shot of currentShots) {
          await loadExistingApplications(selectedClient, selectedJob, selectedSequence, shot);
        }
      }
    } catch (error) {
      console.error('Failed to refresh preview:', error);
    }
  };

  const handleCreateStructure = async () => {
    setLoading(true);
    try {
      // Note: Jobs and sequences will be created by the batch endpoint
      // Count them for the confirmation dialog
      let totalJobsCreated = 0;
      let createdJobPath = '';
      for (const queuedJob of queuedJobs) {
        const jobAlreadyExists = jobs.includes(queuedJob) && !localJobs[selectedClient]?.includes(queuedJob);
        if (!jobAlreadyExists) {
          totalJobsCreated++;
          createdJobPath = `J:\\${selectedClient}\\${queuedJob}`;
        }
      }

      let totalSequencesCreated = 0;
      for (const queuedSeq of queuedSequences) {
        const sequenceAlreadyExists = sequences.includes(queuedSeq.sequence);
        if (!sequenceAlreadyExists) {
          totalSequencesCreated++;
        }
      }
      
      // Create all queued items using batch endpoint
      let totalShotsCreated = 0;
      let totalApplicationsCreated = 0;

      // Collect all operations into a batch
      const batchOperations = [];

      // Add job-only operations (jobs without shots)
      for (const queuedJob of queuedJobs) {
        // Only add if there are no shots for this job (job-only mode)
        const hasShots = Object.keys(queuedShots).length > 0;
        if (!hasShots) {
          batchOperations.push({
            client: selectedClient,
            job: queuedJob,
            sequence: null,
            shots: [],
            applications: []
          });
        }
      }

      for (const sequence of Object.keys(queuedShots)) {
        const shotObjects = queuedShots[sequence] ||[];

        // Count implicitly created sequences
        if (!sequences.includes(sequence) && !queuedSequences.some(qSeq => qSeq.sequence === sequence)) {
          totalSequencesCreated++;
        }

        for (const shotObj of shotObjects) {
          // Check if this shot already exists
          const existingShotsInSequence = allSequenceShots[sequence] || [];
          const shotAlreadyExists = existingShotsInSequence.includes(shotObj.shot);

          // Add to batch operations
          batchOperations.push({
            client: selectedClient,
            job: selectedJob,
            sequence: sequence,
            shots: [shotObj.shot],
            applications: shotObj.applications || []
          });

          // Only count as created if it's a new shot
          if (!shotAlreadyExists) {
            totalShotsCreated++;
          }

          // Count only newly created application folders
          if (shotObj.applications) {
            const shotKey = `${selectedClient}/${selectedJob}/${sequence}/${shotObj.shot}`;
            const existingApps = shotExistingApps[shotKey] || [];

            for (const app of shotObj.applications) {
              // Only count if this app doesn't already exist in the shot
              if (!existingApps.includes(app)) {
                totalApplicationsCreated++;
              }
            }
          }
        }
      }

      // Execute all operations in a single batch call
      if (batchOperations.length > 0) {
        await apiService.createBatch(batchOperations);
      }
      
      // Show success confirmation dialog
      setConfirmationState({
        isOpen: true,
        jobs: totalJobsCreated,
        jobPath: createdJobPath,
        sequences: totalSequencesCreated,
        shots: totalShotsCreated,
        applications: totalApplicationsCreated,
        isError: false,
        message: ''
      });
      
      // Clear backend memory storage for this job
      await apiService.clearQueuedItems(selectedClient, selectedJob);

      // Clear queues
      setQueuedJobs([]);
      setQueuedSequences([]);
      setQueuedShots({});
      
      // Clear temp shot range and temporary entries
      setTempShotRange('');
      setTemporaryShotEntries([]);
      
      // Refresh data with delays to avoid rate limiting
      await loadClients();
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (selectedClient) {
        await loadJobs(selectedClient);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (selectedJob && selectedClient) {
        await loadSequences(selectedClient, selectedJob);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (selectedSequence && selectedClient && selectedJob) {
        await loadShots(selectedClient, selectedJob, selectedSequence);
      }
      
    } catch (error) {
      console.error('Failed to create structure:', error);
      // Show error confirmation dialog
      setConfirmationState({
        isOpen: true,
        jobs: 0,
        jobPath: '',
        sequences: 0,
        shots: 0,
        applications: 0,
        isError: true,
        message: 'Failed to create folder structure. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if we can add application folders to an existing shot
  const canAddAppsToExistingShot = () => {
    if (!selectedSequence || !selectedShot || !selectedClient || !selectedJob) {
      return false;
    }

    // Get list of currently selected apps
    const selectedAppsList = Object.keys(selectedApps).filter(app => selectedApps[app]);
    if (selectedAppsList.length === 0) {
      return false;
    }

    // Handle "All Shots" selection
    if (selectedShot === "All Shots") {
      const existingShots = allSequenceShots[selectedSequence] || [];
      // Can add apps to all shots if there are existing shots
      if (existingShots.length === 0) {
        return false;
      }

      // Check if any shot has at least one new app to add
      for (const shotName of existingShots) {
        const shotKey = `${selectedClient}/${selectedJob}/${selectedSequence}/${shotName}`;
        const existingApps = shotExistingApps[shotKey] || [];
        const hasNewApps = selectedAppsList.some(app => !existingApps.includes(app));
        if (hasNewApps) {
          return true; // At least one shot can accept new apps
        }
      }
      return false; // No shots have new apps to add
    } else {
      // Check if the specific shot exists in the current sequence (on server or in queue)
      const existingShots = allSequenceShots[selectedSequence] || [];
      const shotExistsOnServer = existingShots.includes(selectedShot);

      // Also check if shot exists in queued shots
      const queuedShotsForSeq = queuedShots[selectedSequence] || [];
      const shotExistsInQueue = queuedShotsForSeq.some(s => s.shot === selectedShot);

      const shotExists = shotExistsOnServer || shotExistsInQueue;

      if (!shotExists) {
        return false;
      }

      // Get existing apps from server
      const shotKey = `${selectedClient}/${selectedJob}/${selectedSequence}/${selectedShot}`;
      const existingAppsOnServer = shotExistingApps[shotKey] || [];

      // Get queued apps (apps already queued for this shot)
      const queuedShot = queuedShotsForSeq.find(s => s.shot === selectedShot);
      const queuedApps = queuedShot ? queuedShot.applications : [];

      // Combine existing and queued apps
      const allExistingApps = [...new Set([...existingAppsOnServer, ...queuedApps])];

      // Check if any selected apps are NEW (not already in the shot or queued)
      const hasNewApps = selectedAppsList.some(app => !allExistingApps.includes(app));

      return hasNewApps;
    }
  };

  const user = authService.getUser();

  return (
    <div className="folder-builder">
      <header className="header">
        <h1>The Heist Framework</h1>
        <div className="user-info">
          {user && user.picture && (
            <img 
              src={user.picture} 
              alt="user" 
              className="user-avatar"
            />
          )}
          <span>{user && user.name}</span>
          <button onClick={() => authService.signOut()}>Sign Out</button>
        </div>
      </header>

      <div className="builder-content">
        <div className="main-layout">
          <div className="side-by-side-panel">
            <div className="builder-section">
              <div className="panel-header">
                <h3>BUILDER</h3>
              </div>
          
          <EditableDropdown
            label="Client"
            value={selectedClient}
            options={clients}
            onChange={handleClientChange}
            onCreateNew={() => openDialog('client')}
            placeholder="Select Client"
            addNewText="Add New Client"
          />

          <EditableDropdown
            label="Job"
            value={selectedJob}
            options={jobs}
            onChange={handleJobChange}
            onCreateNew={() => openDialog('job')}
            disabled={!selectedClient}
            placeholder="Select Job"
            addNewText="Add New Job"
          />

          <EditableDropdown
            label="Sequence"
            value={selectedSequence}
            options={sequences}
            onChange={handleSequenceChange}
            onCreateNew={() => openDialog('sequence')}
            disabled={!selectedJob}
            placeholder="Select Sequence"
            addNewText="Add New Sequence"
          />

          <EditableDropdown
            label="Shot"
            value={selectedShot}
            options={shots}
            onChange={handleShotChange}
            onCreateNew={() => openDialog('shot')}
            disabled={!selectedSequence}
            placeholder="Select Shot"
            addNewText="Add New Shot"
            showAllOption={shots.length > 1}
            allOptionValue="All Shots"
            temporaryItems={temporaryShotEntries}
          />

          {/* Application Folders */}
          <div className="dropdown-group">
            <div className="apps-header">
              <label>Application Folders:</label>
              <div className="apps-controls">
                <button 
                  type="button"
                  className="select-all-btn"
                  onClick={handleSelectAllApps}
                >
                  ALL
                </button>
                <button 
                  type="button"
                  className="deselect-all-btn"
                  onClick={handleDeselectAllApps}
                >
                  NONE
                </button>
              </div>
            </div>
            <div className="apps-section">
              <div className="app-checkboxes">
                {appOrder.map(app => (
                  <label key={app} className="app-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedApps[app]}
                      onChange={() => handleAppToggle(app)}
                    />
                    {appDisplayNames[app]}
                  </label>
                ))}
              </div>
            </div>
          </div>

            
            <button
              className="queue-btn"
              onClick={handleQueueItems}
              disabled={
                loading ||
                !selectedClient ||
                !selectedJob ||
                (selectedSequence && (!appsChanged || Object.values(selectedApps).every(app => !app)))
              }
            >
              {loading ? 'Queueing...' : 'Queue Folders'}
            </button>
            </div>
            
            <div className="vertical-divider"></div>
            
            <div className="preview-section">
            <div className="panel-header">
              <h3>PREVIEW</h3>
            </div>
            
            <div className="preview-content">
              {selectedJob ? (
                <div className="job-preview">
                  <div className="job-title">
                    <span>{selectedJob}</span>
                    <button 
                      className="refresh-btn"
                      onClick={() => refreshPreview()}
                      title="Refresh preview"
                      disabled={loading}
                    >
                      ↻
                    </button>
                  </div>
                  
                  <div className="scrollable-content">
                    <div className="folder-structure">
                    {(() => {
                      // Build a complete list of all sequences (existing + queued)
                      const allSequences = [...new Set([...sequences, ...queuedSequences.map(q => q.sequence)])];
                      
                      return allSequences.map(seq => {
                        const isQueuedSequence = queuedSequences.some(q => q.sequence === seq);
                        const isExistingSequence = sequences.includes(seq);
                        
                        // Determine status: queued takes precedence over existing
                        let status = 'existing';
                        if (isQueuedSequence && !isExistingSequence) {
                          status = 'queued';
                        }
                        
                        return (
                          <div key={seq} className="sequence-container">
                            {/* Sequence line */}
                            <div className={`folder-item sequence-item ${status}`}>
                              <div className="sequence-name">
                                <span className="sequence-bracket">›</span>
                                <span className="folder-name">{seq}</span>
                              </div>
                              {status === 'queued' && (
                                <button 
                                  className="remove-btn" 
                                  onClick={() => removeQueuedSequence(queuedSequences.findIndex(q => q.sequence === seq))}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                            
                            {/* Shots for this sequence */}
                            {(() => {
                              // Get existing shots for this sequence from allSequenceShots
                              const existingShotsSync = allSequenceShots[seq] || [];
                              
                              // Get queued shots for this sequence from the queued structure
                              const queuedShotsForSeq = queuedShots[seq] || [];
                              
                              return [...existingShotsSync.map(shot => {
                                        // Check if this existing shot is also queued (has apps being added)
                                        const queuedShot = queuedShotsForSeq.find(queuedShot => queuedShot.shot === shot);
                                        const isQueued = !!queuedShot;
                                        const shotKey = `${selectedClient}/${selectedJob}/${seq}/${shot}`;
                                        const existingApps = shotExistingApps[shotKey] || []; // Get actual existing apps from state
                                        const newApps = queuedShot ? queuedShot.applications : [];
                                        
                                        return { 
                                          shot, 
                                          type: isQueued ? 'queued' : 'existing', 
                                          existingApplications: existingApps,
                                          newApplications: newApps,
                                          applications: isQueued ? [...existingApps, ...newApps.filter(app => !existingApps.includes(app))] : existingApps
                                        };
                                      }),
                                      ...queuedShotsForSeq.filter(shotObj => !existingShotsSync.includes(shotObj.shot)).map(shotObj => ({ 
                                        shot: shotObj.shot, 
                                        type: 'queued', 
                                        existingApplications: [],
                                        newApplications: shotObj.applications,
                                        applications: shotObj.applications 
                                      }))]
                                     .map((shotData, shotIndex) => (
                                <div key={`${seq}-${shotData.shot}-${shotIndex}`}>
                                  <div className={`folder-item shot-item ${shotData.type}`}>
                                    <div className="shot-name">
                                      <span className="shot-bracket">›</span>
                                      {shotData.shot}
                                    </div>
                                    {shotData.type === 'queued' && (
                                      <button 
                                        className="remove-btn" 
                                        onClick={() => removeQueuedShot(seq, shotData.shot)}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  {shotData.applications && shotData.applications.length > 0 && (
                                    <div className={`folder-item app-list ${shotData.type}`}>
                                      <span className="apps-horizontal">
                                        {shotData.applications.map((app, appIndex) => {
                                          const isExistingApp = shotData.existingApplications && shotData.existingApplications.includes(app);
                                          const isNewApp = shotData.newApplications && shotData.newApplications.includes(app);
                                          let appClass = 'app-tag';
                                          
                                          // For queued shots, show different colors for existing vs new apps
                                          if (shotData.type === 'queued') {
                                            if (isNewApp && !isExistingApp) {
                                              appClass += ' app-new'; // Yellow for new apps
                                            } else if (isExistingApp) {
                                              appClass += ' app-existing'; // Grey for existing apps
                                            }
                                          }
                                          
                                          return (
                                            <span key={app} className={appClass}>
                                              {appAbbreviations[app] || app}
                                              {appIndex < shotData.applications.length - 1 && ', '}
                                            </span>
                                          );
                                        })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ));
                            })()}
                          </div>
                        );
                      });
                    })()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-job-selected">
                  Select a Job...
                </div>
              )}
            </div>
            
            <button
              className="create-btn"
              onClick={handleCreateStructure}
              disabled={loading || !selectedJob || (queuedJobs.length === 0 && queuedSequences.length === 0 && Object.keys(queuedShots).length === 0)}
            >
              {loading ? 'Creating...' : 'Create Folders'}
            </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog for creating new items */}
      {dialogState.isOpen && (
        <CreateItemDialog
          isOpen={dialogState.isOpen}
          onClose={closeDialog}
          onSubmit={handleDialogSubmit}
          title={dialogConfigs[dialogState.type]?.title}
          fields={dialogConfigs[dialogState.type]?.fields}
          currentStep={dialogState.step}
        />
      )}

      {/* Confirmation dialog for folder creation results */}
      <ConfirmationDialog
        isOpen={confirmationState.isOpen}
        onClose={() => setConfirmationState({ ...confirmationState, isOpen: false })}
        jobs={confirmationState.jobs}
        jobPath={confirmationState.jobPath}
        sequences={confirmationState.sequences}
        shots={confirmationState.shots}
        applications={confirmationState.applications}
        isError={confirmationState.isError}
        message={confirmationState.message}
      />

      {/* Similar client suggestions dialog */}
      <SimilarClientDialog
        isOpen={similarClientState.isOpen}
        onClose={handleSimilarClientClose}
        inputName={similarClientState.inputName}
        suggestions={similarClientState.suggestions}
        onSelectSuggestion={handleSelectSuggestion}
        onCreateNew={handleCreateNewFromDialog}
      />
    </div>
  );
};

export default FolderStructureBuilder;